import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceDot } from 'recharts';
import { NetWorthSnapshot } from '../types';
import { getJourneyStart, getSnapshotsWithAge, requiredNetWorthAtAge, type SnapshotWithAge } from '../utils/fireJourney';

type Props = {
  currentAge: number;
  retirementAge: number;
  currentNetWorth: number;
  fireTarget: number;
  currencySymbol?: string;
  snapshots?: NetWorthSnapshot[];
};

type JourneyPoint = {
  age: number;
  required: number;
  actual: number | null;
  isCurrent?: boolean;
  isHistorical?: boolean;
};

const ageMonthKey = (age: number) => `${Math.round(age * 12)}`;

const dedupeSnapshotsByAgeMonth = (snapshotsWithAge: SnapshotWithAge[], currentAge: number) => {
  const currentAgePeriod = ageMonthKey(currentAge);
  const latestByPeriod = new Map<string, SnapshotWithAge>();

  snapshotsWithAge.forEach((snapshot) => {
    const period = ageMonthKey(snapshot.age);

    if (period === currentAgePeriod) return;

    const existing = latestByPeriod.get(period);
    const existingTime = existing ? new Date(existing.date).getTime() : Number.NEGATIVE_INFINITY;
    const snapshotTime = new Date(snapshot.date).getTime();

    if (!existing || snapshotTime >= existingTime) {
      latestByPeriod.set(period, snapshot);
    }
  });

  return [...latestByPeriod.values()].sort((a, b) => a.age - b.age);
};

const ActualDot = (props: any) => {
  const { cx, cy, payload } = props;

  if (cx == null || cy == null || payload?.actual == null) return null;

  if (payload.isCurrent) {
    return (
      <g>
        <circle cx={cx} cy={cy} r={14} fill="#38BDF8" opacity="0.16">
          <animate attributeName="r" values="9;17;9" dur="2.4s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.24;0.04;0.24" dur="2.4s" repeatCount="indefinite" />
        </circle>
        <circle cx={cx} cy={cy} r={8} fill="#38BDF8" opacity="0.24" />
        <circle cx={cx} cy={cy} r={5.5} fill="#7DD3FC" stroke="#082f49" strokeWidth={2} />
      </g>
    );
  }

  return <circle cx={cx} cy={cy} r={3.5} fill="#38BDF8" opacity={payload.isHistorical ? 0.45 : 0.75} />;
};

export default function FireJourneyChart({
  currentAge,
  retirementAge,
  currentNetWorth,
  fireTarget,
  currencySymbol = '£',
  snapshots = [],
}: Props) {
  const snapshotsWithAge = getSnapshotsWithAge(snapshots, currentAge);
  const monthlySnapshots = dedupeSnapshotsByAgeMonth(snapshotsWithAge, currentAge);
  const { startAge } = getJourneyStart(monthlySnapshots, currentAge, currentNetWorth);
  const startAgeRounded = Math.floor(startAge);
  const axisStartAge = Math.max(0, startAgeRounded - 1);

  const data: JourneyPoint[] = [];

  const requiredAt = (age: number) =>
    requiredNetWorthAtAge({
      age,
      retirementAge,
      fireTarget,
      currentAge,
      currentNetWorth,
    });

  for (let age = startAgeRounded; age <= retirementAge; age++) {
    data.push({
      age,
      required: requiredAt(age),
      actual: null,
    });
  }

  const actualPoints = [
    ...monthlySnapshots.map((snapshot) => ({
      age: snapshot.age,
      value: snapshot.totalNetWorth,
      isCurrent: false,
    })),
    { age: currentAge, value: currentNetWorth, isCurrent: true },
  ].sort((a, b) => a.age - b.age);

  actualPoints.forEach((point) => {
    const idx = data.findIndex((d) => d.age === point.age);
    const required = requiredAt(point.age);

    if (idx >= 0) {
      data[idx].actual = point.value;
      data[idx].required = required;
      data[idx].isHistorical = !point.isCurrent;
      if (point.isCurrent) data[idx].isCurrent = true;
    } else {
      data.push({
        age: point.age,
        required,
        actual: point.value,
        isCurrent: point.isCurrent,
        isHistorical: !point.isCurrent,
      });
    }
  });

  data.sort((a, b) => a.age - b.age);

  return (
    <div>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={data} margin={{ top: 18, right: 22, left: 4, bottom: 8 }}>
          <XAxis
            dataKey="age"
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#94a3b8', fontSize: 11 }}
            type="number"
            domain={[axisStartAge, retirementAge]}
            allowDecimals={false}
            tickFormatter={(value: number) => value.toFixed(0)}
          />
          <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} width={52} tickFormatter={(v) => `${currencySymbol}${(v / 1000).toFixed(0)}k`} />
          <Tooltip
            wrapperStyle={{ zIndex: 30, pointerEvents: 'none' }}
            allowEscapeViewBox={{ x: true, y: true }}
            contentStyle={{ backgroundColor: '#1e1e26', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
            formatter={(v: number, name: string) => [
              `${currencySymbol}${v.toLocaleString()}`,
              name === 'actual' ? 'Net worth' : 'Required trajectory',
            ]}
            labelFormatter={(label) => `Age ${Number(label).toFixed(1)}`}
          />
          <Line
            type="monotone"
            dataKey="required"
            stroke="#64748b"
            strokeDasharray="4 4"
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="actual"
            stroke="#38BDF8"
            strokeWidth={3}
            dot={<ActualDot />}
            activeDot={false}
            isAnimationActive
            connectNulls
          />
          <ReferenceLine
            y={fireTarget}
            stroke="#F59E0B"
            strokeDasharray="2 2"
            label={{ value: 'FIRE target', position: 'insideTopRight', fill: '#FCD34D', fontSize: 12 }}
          />
          <ReferenceDot
            x={retirementAge}
            y={fireTarget}
            r={5}
            fill="#F59E0B"
            stroke="#FCD34D"
            strokeWidth={2}
            label={{ value: 'Target', position: 'top', fill: '#FCD34D', fontSize: 12 }}
          />
        </LineChart>
      </ResponsiveContainer>

      <div className="mt-3 flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
        <span>Start</span>
        <span className="text-sky-300">Today</span>
        <span className="text-amber-300">Target</span>
      </div>

      {monthlySnapshots.length === 0 && (
        <p className="text-xs text-slate-500 mt-2 text-center md:text-left">Tracking your journey from today.</p>
      )}
    </div>
  );
}
